// 管理员认证服务 - 对接后端Token鉴权

const TOKEN_KEY = 'admin_token';
const ADMIN_KEY = 'admin_info';

const API_BASE = '';

export interface AdminInfo {
  username: string;
  name: string;
  role: 'super' | 'admin';
}

export const adminAuth = {
  // 检查是否已登录
  isLoggedIn(): boolean {
    return !!this.getToken();
  },

  // 获取Token
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },

  // 获取管理员信息
  getAdminInfo(): AdminInfo | null {
    const stored = localStorage.getItem(ADMIN_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  },

  // 登录
  async login(username: string, password: string): Promise<{ success: boolean; error?: string }> {
    try {
      const res = await fetch(`${API_BASE}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      const data = await res.json();

      if (data.success && data.token) {
        localStorage.setItem(TOKEN_KEY, data.token);
        localStorage.setItem(ADMIN_KEY, JSON.stringify(data.admin));
        return { success: true };
      } else {
        return { success: false, error: data.error || '登录失败' };
      }
    } catch (e) {
      return { success: false, error: '网络错误，请稍后重试' };
    }
  },

  // 登出
  logout(): void {
    // 通知后端注销Token（可选，失败不影响本地登出）
    const token = this.getToken();
    if (token) {
      fetch(`${API_BASE}/api/admin/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      }).catch(() => {});
    }

    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ADMIN_KEY);
    // 兼容旧的admin_auth
    localStorage.removeItem('admin_auth');
  },

  // 获取请求头（用于API调用）
  getAuthHeaders(): Record<string, string> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  },

  // 检查是否是超级管理员
  isSuperAdmin(): boolean {
    const admin = this.getAdminInfo();
    return admin?.role === 'super';
  },

  // 首次设置管理员（系统初始化时调用）
  async setup(username: string, password: string, name?: string): Promise<{ success: boolean; error?: string }> {
    try {
      const res = await fetch(`${API_BASE}/api/admin/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, name: name || username }),
      });

      const data = await res.json();

      if (data.success) {
        return { success: true };
      } else {
        return { success: false, error: data.error || '设置失败' };
      }
    } catch (e) {
      return { success: false, error: '网络错误，请稍后重试' };
    }
  },

  // 添加管理员（需要超级管理员权限）
  async addAdmin(username: string, password: string, name?: string, role: 'super' | 'admin' = 'admin'): Promise<{ success: boolean; error?: string }> {
    const token = this.getToken();
    if (!token) {
      return { success: false, error: '未登录' };
    }

    try {
      const res = await fetch(`${API_BASE}/api/admin/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ username, password, name: name || username, role }),
      });

      const data = await res.json();

      if (data.success) {
        return { success: true };
      } else {
        return { success: false, error: data.error || '添加失败' };
      }
    } catch (e) {
      return { success: false, error: '网络错误' };
    }
  },

  // 删除管理员（需要超级管理员权限）
  async removeAdmin(username: string): Promise<{ success: boolean; error?: string }> {
    const token = this.getToken();
    if (!token) {
      return { success: false, error: '未登录' };
    }

    try {
      const res = await fetch(`${API_BASE}/api/admin/remove`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ username }),
      });

      const data = await res.json();

      if (data.success) {
        return { success: true };
      } else {
        return { success: false, error: data.error || '删除失败' };
      }
    } catch (e) {
      return { success: false, error: '网络错误' };
    }
  },

  // 获取管理员列表
  async listAdmins(): Promise<{ success: boolean; admins?: any[]; error?: string }> {
    const token = this.getToken();
    if (!token) {
      return { success: false, error: '未登录' };
    }

    try {
      const res = await fetch(`${API_BASE}/api/admin/list`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await res.json();
      return data;
    } catch (e) {
      return { success: false, error: '网络错误' };
    }
  },
};

export default adminAuth;
